import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { Camera as CameraIcon } from 'lucide-react';
import { Input } from '../components/ui/input.js';
import { Card, CardBody } from '../components/ui/card.js';
import { Pill } from '../components/ui/pill.js';
import { QueryError } from '../components/ui/query-error.js';
import { BarcodeScanner } from '../components/BarcodeScanner.js';
import { ApiError, api, type InventoryItemSummary } from '../lib/api.js';

interface InventorySearchParams {
  whse?: string;
  q?: string;
}

/**
 * Mobile inventory lookup.
 *
 * Search params live in the URL so:
 *   - shared links land users on the same query
 *   - back navigation restores typing
 *   - bookmarks work
 *
 * Debounce on the q input (250ms) so we don't hammer Spire on every keystroke.
 */
export function InventorySearchScreen() {
  const navigate = useNavigate({ from: '/inventory' });
  const search = useSearch({ from: '/inventory' }) as InventorySearchParams;
  const whse = search.whse ?? '';
  const q = search.q ?? '';

  const [qDraft, setQDraft] = useState(q);
  const [whseDraft, setWhseDraft] = useState(whse);
  const [scanOpen, setScanOpen] = useState(false);

  const handleScan = useCallback(
    (text: string) => {
      setScanOpen(false);
      const cleaned = text.trim();
      setQDraft(cleaned);
      // Push directly into the URL so the query fires immediately without
      // waiting for the typing debounce.
      void navigate({ search: { whse: whseDraft || undefined, q: cleaned || undefined } });
    },
    [navigate, whseDraft],
  );

  // Debounce drafts before syncing into URL state. q has natural typing
  // cadence; whse changes infrequently but use the same debounce so a fast
  // typer doesn't fire mid-token searches against Spire.
  useEffect(() => {
    if (qDraft === q && whseDraft === whse) return;
    const id = setTimeout(() => {
      void navigate({
        search: {
          whse: whseDraft || undefined,
          q: qDraft || undefined,
        },
      });
    }, 250);
    return () => clearTimeout(id);
  }, [qDraft, whseDraft, q, whse, navigate]);

  const hasFilter = Boolean(whse || q);

  const query = useQuery({
    queryKey: ['inventory', 'search', whse, q],
    queryFn: ({ signal }) =>
      api.inventory.search(
        { whse: whse || undefined, q: q || undefined, limit: 50 },
        signal,
      ),
    enabled: hasFilter,
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Inventory lookup</h1>
      <div className="grid grid-cols-[120px_1fr] gap-3">
        <Input
          aria-label="Warehouse code (optional)"
          placeholder="Whse (e.g. 00)"
          value={whseDraft}
          onChange={(e) => setWhseDraft(e.target.value.trim().toUpperCase())}
          className="font-mono"
          autoCapitalize="characters"
          autoCorrect="off"
        />
        <div className="relative">
          <Input
            aria-label="Search part number or description"
            type="search"
            placeholder="Part # or description"
            value={qDraft}
            // Free-text search (Spire ?q=) — any case, several words,
            // part-number fragments or description.
            onChange={(e) => setQDraft(e.target.value)}
            autoCorrect="off"
            autoComplete="off"
            autoFocus
            className="pr-11"
          />
          <button
            type="button"
            onClick={() => setScanOpen(true)}
            aria-label="Scan a barcode"
            className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-md text-brand hover:bg-brand-50 active:bg-brand-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
          >
            <CameraIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <BarcodeScanner
        open={scanOpen}
        onResult={handleScan}
        onClose={() => setScanOpen(false)}
      />

      {!hasFilter ? (
        <Card>
          <CardBody>
            <div className="text-sm text-ink-muted">
              Type a part number or description to search across all warehouses, or set a warehouse code (e.g.{' '}
              <span className="font-mono">00</span>) to browse one location.
            </div>
          </CardBody>
        </Card>
      ) : (
        <Results query={query} whse={whse} q={q} />
      )}
    </div>
  );
}

interface ResultsProps {
  query: ReturnType<typeof useQuery<InventoryItemSummary[], ApiError>>;
  whse: string;
  q: string;
}

function Results({ query, whse, q }: ResultsProps) {
  if (query.isError) {
    return (
      <QueryError
        title={
          query.error?.kind === 'auth' ? 'Sign in to view inventory' : "Couldn't load inventory"
        }
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  }

  if (query.isPending) {
    return <SkeletonList />;
  }

  const items = query.data ?? [];
  if (items.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="text-sm text-ink-muted">
            No items match{q ? <> <strong>"{q}"</strong></> : ''}
            {whse ? (
              <> in warehouse <span className="font-mono">{whse}</span></>
            ) : ''}.
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item.spireId}>
          <ResultCard item={item} />
        </li>
      ))}
    </ul>
  );
}

function ResultCard({ item }: { item: InventoryItemSummary }) {
  const available = Number(item.availableQty);
  const isObsolete = item.status === 'obsolete';
  // Stock-tone matches the per-warehouse stockbar on the detail page so the
  // visual signal is consistent across screens.
  const stockPill =
    available <= 0
      ? { variant: 'danger' as const, label: 'Out' }
      : available <= 3
        ? { variant: 'warning' as const, label: 'Low' }
        : { variant: 'success' as const, label: 'In stock' };

  return (
    <Link
      to="/inventory/$id"
      params={{ id: String(item.spireId) }}
      className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 rounded-lg"
    >
      <Card className="transition-shadow hover:shadow-md active:shadow-sm">
        <CardBody className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-semibold text-ink">{item.partNo}</span>
              <Pill variant="neutral">whse {item.whseCode}</Pill>
              {isObsolete && <Pill variant="neutral">obsolete</Pill>}
            </div>
            <div className="mt-0.5 text-sm text-ink-muted truncate">
              {item.description ?? '(no description)'}
            </div>
          </div>
          <div className="text-right shrink-0 space-y-1">
            <Pill variant={stockPill.variant} dot>
              {stockPill.label} · {item.availableQty} {item.uom ?? ''}
            </Pill>
            <div className="text-xs text-ink-muted font-mono">${item.defaultSellPrice}</div>
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}

function SkeletonList() {
  return (
    <ul className="space-y-2" aria-busy="true" aria-live="polite">
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i}>
          <Card>
            <CardBody className="flex items-start gap-3">
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 bg-surface-muted rounded animate-pulse" />
                <div className="h-3 w-3/4 bg-surface-muted rounded animate-pulse" />
              </div>
              <div className="h-4 w-12 bg-surface-muted rounded animate-pulse" />
            </CardBody>
          </Card>
        </li>
      ))}
    </ul>
  );
}
